import React, { useState, useEffect } from 'react';
import { Download, Package, CheckCircle, ExternalLink, Lock, Sparkles } from 'lucide-react';
import { api } from '../services/api';

const PluginDownloadPage = ({ user }) => {
  const [downloading, setDownloading] = useState(false);
  const [userAccess, setUserAccess] = useState({
    has_plugin: user?.has_plugin,
    has_course: user?.has_course,
    course_override: user?.course_override
  });

  // 🔄 POLLING: Verifica a cada 2s se admin liberou o acesso
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const me = await api.getMe();
        const newAccess = {
          has_plugin: me?.has_plugin,
          has_course: me?.has_course,
          course_override: me?.course_override
        };

        // Se mudou, atualiza e recarrega página
        if (newAccess.has_plugin !== userAccess.has_plugin ||
            newAccess.has_course !== userAccess.has_course || 
            newAccess.course_override !== userAccess.course_override) {
          console.log('🎉 Acesso ao Plugin liberado! Recarregando...');
          setUserAccess(newAccess);
          setTimeout(() => window.location.reload(), 500);
        }
      } catch (err) {
        console.error('Erro no polling:', err);
      }
    }, 2000); // A cada 2 segundos

    return () => clearInterval(interval);
  }, [userAccess]);

  // Verifica se tem acesso ao plugin (plano 87 ou 149)
  const hasPluginAccess = userAccess.has_plugin === true || userAccess.has_course === true || userAccess.course_override === true;

  console.log('🔍 PluginDownloadPage - user completo:', user);
  console.log('🔍 has_plugin:', userAccess.has_plugin);
  console.log('🔍 has_course:', userAccess.has_course);
  console.log('🔍 course_override:', userAccess.course_override);
  console.log('🔍 hasPluginAccess:', hasPluginAccess);

  const handleDownload = async () => {
    if (!hasPluginAccess) return;
    try {
      const token = localStorage.getItem('user_token') || localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/plugin/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.url) window.open(data.url, '_blank');
      } else {
        console.error('Erro ao obter link de download:', response.status);
      }
    } catch (err) {
      console.error('Erro ao obter link de download:', err);
    }
  };

  if (!hasPluginAccess) {
    // Upsell para quem não tem acesso
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#05050A] via-[#0A0A12] to-[#05050A] text-white p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 px-4 py-2 rounded-full border border-purple-500/30 mb-4">
              <Sparkles size={16} className="text-purple-400" />
              <span className="text-sm font-bold text-purple-300">UPGRADE PREMIUM</span>
            </div>
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Plugin Editor Premium
            </h1>
            <p className="text-gray-400 text-lg">
              Integração direta com After Effects e Premiere Pro
            </p>
          </div>

          {/* Card de Bloqueio */}
          <div className="bg-[#0F0F1A] rounded-2xl border border-gray-800 overflow-hidden">
            <div className="relative h-64 bg-gradient-to-br from-purple-900/20 to-blue-900/20 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                <div className="text-center">
                  <Lock size={64} className="text-gray-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-300 mb-2">
                    Conteúdo Exclusivo Premium
                  </h3>
                  <p className="text-gray-500">
                    Disponível apenas no plano completo
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Features */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4">O que você ganha:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'Plugin nativo para After Effects',
                    'Plugin nativo para Premiere Pro',
                    'Acesso direto à biblioteca dentro do editor',
                    'Download de cenas com 1 clique',
                    'Pré-visualização integrada',
                    'Atualizações automáticas',
                    'Suporte técnico prioritário',
                    'Acesso ao curso completo'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-xl p-6 border border-purple-500/20">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h4 className="text-lg font-bold mb-1">Faça upgrade agora!</h4>
                    <p className="text-gray-400 text-sm">
                      Acesso completo à plataforma + Plugin + Curso
                    </p>
                  </div>
                  <a
                    href="https://pay.kiwify.com.br/7t7Vix1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3 rounded-lg font-bold transition-all flex items-center gap-2 shadow-lg shadow-purple-500/30"
                  >
                    <span>Fazer Upgrade</span>
                    <ExternalLink size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Página de download para quem tem acesso
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#05050A] via-[#0A0A12] to-[#05050A] text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600/20 to-emerald-600/20 px-4 py-2 rounded-full border border-green-500/30 mb-4">
            <CheckCircle size={16} className="text-green-400" />
            <span className="text-sm font-bold text-green-300">ACESSO PREMIUM ATIVO</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
            Plugin Editor Premium
          </h1>
          <p className="text-gray-400 text-lg">
            Baixe o plugin e integre sua biblioteca ao editor
          </p>
        </div>

        {/* Card de Download */}
        <div className="bg-[#0F0F1A] rounded-2xl border border-gray-800 overflow-hidden mb-6">
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 p-8 border-b border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <Package size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-1">Plugin Editor Premium</h2>
                <p className="text-gray-400">Versão 1.2.0 • After Effects & Premiere Pro</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-6 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-3 shadow-lg shadow-green-500/30"
            >
              {downloading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Baixando...</span>
                </>
              ) : (
                <>
                  <Download size={20} />
                  <span>Baixar Plugin (ZIP)</span>
                </>
              )}
            </button>

            <p className="text-center text-gray-500 text-sm mt-4">
              Arquivo ZIP • ~5 MB • Compatível com Windows e macOS
            </p>
          </div>
        </div>

        {/* Instruções de Instalação */}
        <div className="bg-[#0F0F1A] rounded-2xl border border-gray-800 p-8">
          <h3 className="text-xl font-bold mb-6">📖 Instruções de Instalação</h3>
          
          <div className="space-y-6">
            {/* After Effects */}
            <div>
              <h4 className="font-bold text-green-400 mb-3 flex items-center gap-2">
                <CheckCircle size={18} />
                After Effects
              </h4>
              <ol className="space-y-2 text-gray-300 text-sm">
                <li>1. Extraia o arquivo ZIP</li>
                <li>2. Copie o arquivo <code className="bg-black/50 px-2 py-1 rounded">com.biblioteca.diorgy.panel</code></li>
                <li>3. Cole em: <code className="bg-black/50 px-2 py-1 rounded">C:\Program Files (x86)\Common Files\Adobe\CEP\extensions</code></li>
                <li>4. Reinicie o After Effects</li>
                <li>5. Acesse: <strong>Window → Editor Premium</strong></li>
              </ol>
            </div>

            {/* Premiere Pro */}
            <div>
              <h4 className="font-bold text-blue-400 mb-3 flex items-center gap-2">
                <CheckCircle size={18} />
                Premiere Pro
              </h4>
              <ol className="space-y-2 text-gray-300 text-sm">
                <li>1. Extraia o arquivo ZIP</li>
                <li>2. Copie o arquivo <code className="bg-black/50 px-2 py-1 rounded">com.biblioteca.diorgy.panel.premiere</code></li>
                <li>3. Cole em: <code className="bg-black/50 px-2 py-1 rounded">C:\Program Files (x86)\Common Files\Adobe\CEP\extensions</code></li>
                <li>4. Reinicie o Premiere Pro</li>
                <li>5. Acesse: <strong>Window → Extensions → Editor Premium</strong></li>
              </ol>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-300 text-sm">
              <strong>💡 Dica:</strong> Se o plugin não aparecer, certifique-se de que está usando uma versão compatível do After Effects/Premiere Pro (CC 2019 ou superior).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PluginDownloadPage;
